import { EncounterRegionNoteModel } from 'src/app/api/proxy/practice/models/encounter-region-note-model';
import { PatientTreatmentQueuePartFindingModel } from 'src/app/api/proxy/practice/models/patient-treatment-queue-part-finding-model';
import { PatientTreatmentQueuePartItemModel } from 'src/app/api/proxy/practice/models/patient-treatment-queue-part-item-model';
import { PatientTreatmentQueueTreatmentNoteModel } from 'src/app/api/proxy/practice/models/patient-treatment-queue-treatment-note-model';
import { PartFindingModel } from 'src/app/api/proxy/practice/models/part-finding-model';
import {
  legacyFindingsToTreatmentState,
  partDataToPartFindingModels,
} from 'src/app/components/soap/part-finding.mapper';
import { cloneRegionNotesForDuplicate } from 'src/app/services/treatment-service/pending-soap.util';
import { ITreatment } from 'src/app/services/ble/ITreatment';
import { ITreatmentPlayListItem } from 'src/app/pages/treatment/treatment.page';

/** Map queue part DTOs into Adjust playlist rows (no placeholder, ordered). */
export function queuePartsToPlaylist(
  parts: PatientTreatmentQueuePartItemModel[] | null | undefined,
): ITreatmentPlayListItem[] {
  if (!parts?.length) {
    return [];
  }

  return [...parts]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((part) => (part.partId ?? 0) > 0)
    .map((part) => ({
      partId: part.partId!,
      maskId: part.maskId,
      partFindings: part.partFindings ?? null,
      vector: part.vector ?? null,
      side: part.side ?? null,
      treatments: [],
    }));
}

/** Map Adjust playlist rows to queue part DTOs, excluding the trailing placeholder. */
export function playlistToQueueParts(
  playlist: ITreatmentPlayListItem[] | null | undefined,
): PatientTreatmentQueuePartItemModel[] {
  if (!playlist?.length) {
    return [];
  }

  const parts: PatientTreatmentQueuePartItemModel[] = [];
  let order = 0;

  for (const item of playlist) {
    const partId = item.partId ?? item.bodyPart?.partId ?? 0;
    if (partId <= 0) {
      continue;
    }

    const maskId =
      item.maskId ??
      item.bodyPart?.selectedMaskId ??
      item.bodyPart?.masks?.[0]?.maskId ??
      0;

    parts.push({
      order: order++,
      partId,
      maskId,
      side: item.side ?? item.bodyPart?.side ?? null,
      partFindings: item.partFindings ?? null,
      vector: item.vector ?? null,
      partName: item.bodyPart?.partName ?? null,
      partListName: item.bodyPart?.partListName ?? null,
    });
  }

  return parts;
}

function playlistRowTreatmentState(
  row: ITreatmentPlayListItem,
): { description: string | null; partFindings: PartFindingModel[] | null } | null {
  const description = (
    row.treatmentNoteDescription
    ?? row.treatments?.[0]?.treatmentNoteDescription
    ?? ''
  ).trim() || null;
  const legacyFindings = row.findings ?? row.treatments?.[0]?.findings;
  const state = legacyFindings?.length
    ? legacyFindingsToTreatmentState(legacyFindings)
    : null;
  const partFindings = state
    ? partDataToPartFindingModels(state.partData)
    : partDataToPartFindingModels(null);
  const resolvedDescription = description ?? (state?.treatmentNote?.trim() || null);
  const resolvedFindings = partFindings.length ? partFindings : null;

  if (!resolvedDescription && !resolvedFindings?.length) {
    return null;
  }

  return {
    description: resolvedDescription,
    partFindings: resolvedFindings,
  };
}

/** Serialize per-level treatment notes for the queue-level JSON blob. */
export function playlistToQueuedTreatmentNotes(
  playlist: ITreatmentPlayListItem[] | null | undefined,
  options?: { onlyPartKeys?: Iterable<string> | null },
): PatientTreatmentQueueTreatmentNoteModel[] | null {
  if (!playlist?.length) {
    return null;
  }

  const onlyKeys = options?.onlyPartKeys ? new Set(options.onlyPartKeys) : null;
  const byKey = new Map<string, PatientTreatmentQueueTreatmentNoteModel>();
  for (const item of playlist) {
    const partId = item.partId ?? item.bodyPart?.partId ?? 0;
    if (partId <= 0) {
      continue;
    }

    const side = item.side ?? item.bodyPart?.side ?? null;
    const key = playlistPartIdentityKey({ partId, side });
    if (onlyKeys && !onlyKeys.has(key)) {
      continue;
    }

    const state = playlistRowTreatmentState(item);
    if (!state) {
      continue;
    }

    byKey.set(key, {
      partId,
      side,
      description: state.description,
      partFindings: state.partFindings,
    });
  }

  const result = [...byKey.values()];
  return result.length ? result : null;
}

/** @deprecated Use playlistToQueuedTreatmentNotes. */
export function playlistToQueuedFindings(
  playlist: ITreatmentPlayListItem[] | null | undefined,
  options?: { onlyPartKeys?: Iterable<string> | null },
): PatientTreatmentQueuePartFindingModel[] | null {
  const treatmentNotes = playlistToQueuedTreatmentNotes(playlist, options);
  if (!treatmentNotes?.length) {
    return null;
  }

  return treatmentNotes.map((note) => ({
    partId: note.partId,
    side: note.side,
    findings: [
      ...(note.partFindings ?? []),
      ...(note.description ? [{ description: note.description }] : []),
    ],
  }));
}

export function cloneQueuedRegionNotes(
  regionNotes: EncounterRegionNoteModel[] | null | undefined,
): EncounterRegionNoteModel[] | null {
  return cloneRegionNotesForDuplicate(regionNotes);
}

/** Copy the current level's note state onto a pulse so it persists with the encounter. */
export function copyPlaylistFindingsOntoTreatment(
  treatment: Pick<ITreatment, 'partFindings' | 'findings' | 'treatmentNoteDescription'>,
  playlistRow?: Pick<
    ITreatmentPlayListItem,
    'partFindings' | 'findings' | 'treatmentNoteDescription'
  > | null,
): void {
  if (!playlistRow) {
    return;
  }
  treatment.partFindings = playlistRow.partFindings ?? treatment.partFindings ?? null;
  treatment.findings = playlistRow.findings ?? treatment.findings ?? null;
  treatment.treatmentNoteDescription =
    playlistRow.treatmentNoteDescription ?? treatment.treatmentNoteDescription ?? null;
}

function treatmentNoteToPlaylistRowState(
  note: PatientTreatmentQueueTreatmentNoteModel,
): Pick<ITreatmentPlayListItem, 'findings' | 'treatmentNoteDescription'> {
  const description = (note.description ?? '').trim() || null;
  const partFindings = note.partFindings ?? [];
  if (!description && !partFindings.length) {
    return { treatmentNoteDescription: null, findings: null };
  }

  return {
    treatmentNoteDescription: description,
    findings: partFindings.length ? partFindings : null,
  };
}

/** Attach queued treatment notes onto playlist rows by partId + normalized side. */
export function applyQueuedTreatmentNotesToPlaylist(
  playlist: ITreatmentPlayListItem[] | null | undefined,
  treatmentNotes: PatientTreatmentQueueTreatmentNoteModel[] | null | undefined,
  legacyFindings?: PatientTreatmentQueuePartFindingModel[] | null | undefined,
): ITreatmentPlayListItem[] {
  const rows = playlist ?? [];
  const notes = treatmentNotes?.length
    ? treatmentNotes
    : (legacyFindings ?? [])
        .filter((entry) => (entry.partId ?? 0) > 0 && entry.findings?.length)
        .map((entry) => {
          const state = legacyFindingsToTreatmentState(entry.findings);
          return {
            partId: entry.partId,
            side: entry.side,
            description: state.treatmentNote || null,
            partFindings: partDataToPartFindingModels(state.partData),
          };
        });

  if (!notes.length) {
    return rows;
  }

  const byKey = new Map<string, PatientTreatmentQueueTreatmentNoteModel>();
  for (const entry of notes) {
    if ((entry.partId ?? 0) <= 0) {
      continue;
    }
    byKey.set(
      playlistPartIdentityKey({ partId: entry.partId, side: entry.side }),
      entry,
    );
  }

  return rows.map((item) => {
    const queued = byKey.get(playlistPartIdentityKey(item));
    if (!queued) {
      return item;
    }
    const mapped = treatmentNoteToPlaylistRowState(queued);
    return {
      ...item,
      treatmentNoteDescription: mapped.treatmentNoteDescription,
      findings: mapped.findings ?? item.findings,
    };
  });
}

/** @deprecated Use applyQueuedTreatmentNotesToPlaylist. */
export function applyQueuedFindingsToPlaylist(
  playlist: ITreatmentPlayListItem[] | null | undefined,
  findings: PatientTreatmentQueuePartFindingModel[] | null | undefined,
): ITreatmentPlayListItem[] {
  return applyQueuedTreatmentNotesToPlaylist(playlist, null, findings);
}

function playlistRowHasSavedFindings(row: ITreatmentPlayListItem): boolean {
  return !!playlistRowTreatmentState(row);
}

export function playlistHasAssignedParts(
  playlist: ITreatmentPlayListItem[] | null | undefined,
): boolean {
  return playlistToQueueParts(playlist).length > 0;
}

/**
 * Fresh queued visits replace the playlist. Existing encounter rows are kept only when
 * resuming the queue's linked encounter so remaining queued parts can be appended.
 */
export function shouldKeepExistingPlaylistWhenHydratingQueue(options: {
  isResumingLinkedEncounter: boolean;
  playlistHasAssignedParts: boolean;
}): boolean {
  return options.isResumingLinkedEncounter && options.playlistHasAssignedParts;
}

/** True when Adjust should recapture playlist from the queuedParts input (ignore clears). */
export function shouldCaptureQueuedPartsInput(
  parts: PatientTreatmentQueuePartItemModel[] | null | undefined,
): boolean {
  return !!parts?.length;
}

export function normalizeQueuePartSide(side?: string | null): string {
  const value = (side ?? '').trim().toUpperCase();
  if (value === 'LEFT' || value === 'L') {
    return 'L';
  }
  if (value === 'RIGHT' || value === 'R') {
    return 'R';
  }
  if (value === 'X' || value === 'BILATERAL') {
    return 'X';
  }
  return value;
}

/** Identity for matching queued parts against encounter playlist rows. */
export function playlistPartIdentityKey(item: {
  partId?: number | null;
  side?: string | null;
  bodyPart?: { side?: string | null } | null;
}): string {
  const partId = item.partId ?? 0;
  const side = normalizeQueuePartSide(item.side ?? item.bodyPart?.side ?? null);
  return `${partId}|${side}`;
}

/**
 * Queued playlist rows that are not already on the encounter playlist
 * (treated or assigned), so Adjust can append remaining work.
 */
export function queuedPlaylistItemsNotOnPlaylist(
  queuedItems: ITreatmentPlayListItem[] | null | undefined,
  existingPlaylist: ITreatmentPlayListItem[] | null | undefined,
): ITreatmentPlayListItem[] {
  const existingKeys = new Set(
    (existingPlaylist ?? [])
      .filter((row) => (row.partId ?? 0) > 0)
      .map(playlistPartIdentityKey),
  );

  return (queuedItems ?? []).filter(
    (item) => (item.partId ?? 0) > 0 && !existingKeys.has(playlistPartIdentityKey(item)),
  );
}

/**
 * Rebuild the queued playlist in queue order, copying treatments from matching
 * encounter rows so a resume shows work already completed on those parts.
 *
 * Treated level (playlist row already has adjustments): encounter findings win.
 * Untreated level: queue findings stay on the row.
 */
export function mergeQueuedPlaylistWithExistingTreatments(
  queuedItems: ITreatmentPlayListItem[] | null | undefined,
  existingPlaylist: ITreatmentPlayListItem[] | null | undefined,
): ITreatmentPlayListItem[] {
  const existingByKey = new Map<string, ITreatmentPlayListItem>();
  for (const row of existingPlaylist ?? []) {
    if ((row.partId ?? 0) <= 0) {
      continue;
    }
    existingByKey.set(playlistPartIdentityKey(row), row);
  }

  const usedKeys = new Set<string>();
  const merged: ITreatmentPlayListItem[] = [];

  for (const item of queuedItems ?? []) {
    if ((item.partId ?? 0) <= 0) {
      continue;
    }

    const key = playlistPartIdentityKey(item);
    const existing = existingByKey.get(key);
    usedKeys.add(key);

    if (existing?.treatments?.length) {
      const useEncounterFindings = playlistRowHasSavedFindings(existing);
      merged.push({
        ...item,
        treatments: existing.treatments,
        partFindings: useEncounterFindings
          ? (existing.partFindings ?? existing.treatments[0]?.partFindings ?? item.partFindings)
          : (item.partFindings ?? existing.partFindings),
        findings: useEncounterFindings
          ? (existing.findings ?? existing.treatments[0]?.findings)
          : item.findings,
        treatmentNoteDescription: useEncounterFindings
          ? (
            existing.treatmentNoteDescription
            ?? existing.treatments[0]?.treatmentNoteDescription
            ?? item.treatmentNoteDescription
          )
          : item.treatmentNoteDescription,
        vector: existing.vector ?? item.vector,
        side: existing.side ?? item.side,
        maskId: existing.maskId ?? item.maskId,
        bodyPart: existing.bodyPart ?? item.bodyPart,
      });
      continue;
    }

    merged.push({
      ...item,
      treatments: item.treatments ?? [],
    });
  }

  for (const row of existingPlaylist ?? []) {
    if ((row.partId ?? 0) <= 0) {
      continue;
    }
    const key = playlistPartIdentityKey(row);
    if (usedKeys.has(key) || !row.treatments?.length) {
      continue;
    }
    merged.push(row);
  }

  return merged;
}

/** True when Adjust already set up a fresh queue and must still load the linked encounter. */
export function shouldReloadLinkedEncounterAfterSetup(options: {
  linkedEncounterId?: string | null;
  isSetupComplete: boolean;
  currentEncounterId?: string | null;
}): boolean {
  return (
    !!options.linkedEncounterId &&
    options.isSetupComplete &&
    options.currentEncounterId !== options.linkedEncounterId
  );
}

type QueueDraftPartIdentity = {
  partId: number;
  side: string | null;
  partFindings: string | null;
  vector: string | null;
};

function toQueueDraftPartIdentity(
  part: PatientTreatmentQueuePartItemModel,
): QueueDraftPartIdentity {
  return {
    partId: part.partId ?? 0,
    side: part.side ?? null,
    partFindings: part.partFindings ?? null,
    vector: part.vector ?? null,
  };
}

/** Compares queued part identity in order, ignoring mask/name fields resolved from anatomy. */
export function queueDraftPartsMatch(
  left: PatientTreatmentQueuePartItemModel[] | null | undefined,
  right: PatientTreatmentQueuePartItemModel[] | null | undefined,
): boolean {
  const a = [...(left ?? [])]
    .filter((part) => (part.partId ?? 0) > 0)
    .sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
    .map(toQueueDraftPartIdentity);
  const b = [...(right ?? [])]
    .filter((part) => (part.partId ?? 0) > 0)
    .sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
    .map(toQueueDraftPartIdentity);

  if (a.length !== b.length) {
    return false;
  }

  return a.every(
    (part, index) =>
      part.partId === b[index].partId &&
      part.side === b[index].side &&
      part.partFindings === b[index].partFindings &&
      part.vector === b[index].vector,
  );
}
